import { useSuspenseQuery } from "@apollo/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Copyable,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { RelatedLedgerTransactions } from "~/components/RelatedLedgerTransactions";
import { Details } from "~/components/TransferDetail/Details";
import { graphql } from "~/gql/client";
import type { TransferDetail_CalastoneOrderFragment } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

const tabValues = ["details", "transactions"] as const;
type TabValue = (typeof tabValues)[number];

export const Route = createFileRoute("/__auth/transfers_/$transferId")({
  head: () => opsHead("Transfer"),
  validateSearch: z.object({
    tab: z.enum(tabValues).optional().catch(undefined),
  }),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const calastoneOrderColumns: ColumnDef<TransferDetail_CalastoneOrderFragment>[] =
  [
    {
      id: "shortRef",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Ref" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-tertiary">
          <Copyable copyText={row.original.id}>
            #{row.original.shortRef}
          </Copyable>
        </span>
      ),
    },
    {
      id: "status",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => (
        <span className="text-secondary text-sm">
          {prettyPrintEnum(row.original.status)}
        </span>
      ),
    },
    {
      id: "calastoneId",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Calastone ID" />
      ),
      cell: ({ row }) =>
        row.original.calastoneId ? (
          <span className="font-mono text-sm">{row.original.calastoneId}</span>
        ) : (
          <span className="text-tertiary">—</span>
        ),
    },
    {
      id: "createdAt",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => (
        <DateUI date={row.original.createdAt} variant="DATETIME_TZ" />
      ),
    },
    {
      id: "updatedAt",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Updated" />
      ),
      cell: ({ row }) => (
        <DateUI date={row.original.updatedAt} variant="DATETIME_TZ" />
      ),
    },
  ];

function View() {
  const { transferId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const activeTab: TabValue = tab ?? "details";

  const setTab = (value: string) => {
    void navigate({
      search: (prev: { [key: string]: string }) => ({
        ...prev,
        tab: value as TabValue,
      }),
    });
  };

  const { data: gqlData, error } = useSuspenseQuery(
    graphql(
      `query TransferDetailPage($id: ID!) {
        node (id: $id) {
          id
          ... on Transfer {
            id
            paymentReference
            ledgerTransactions {
              id
              ...RelatedLedgerTransactions
            }
            calastoneOrders {
              id
              ...TransferDetail_CalastoneOrder
            }
          }
          ...TransferDetailPage_Details
        }
      }

      fragment TransferDetail_CalastoneOrder on CalastoneOrder {
        id
        shortRef
        status
        calastoneId
        reason
        createdAt
        updatedAt
      }`,
    ),
    {
      variables: { id: transferId },
    },
  );

  if (!gqlData || gqlData.node?.__typename !== "Transfer") {
    throw new NotFoundError();
  }

  const transfer = gqlData.node;
  const calastoneOrders = transfer.calastoneOrders ?? [];

  useOpsTitle(`Transfer #${transfer.paymentReference}`);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Transfer #${transfer.paymentReference}`}
        subtitle={<div className="font-mono">{transfer.id}</div>}
      />
      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList size="sm" className="mb-6">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <div className="flex flex-col gap-6">
            <Details transfer={transfer} error={error} />
            {calastoneOrders.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Calastone Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <DataTable
                    data={calastoneOrders}
                    columns={calastoneOrderColumns}
                    isLoadingData={false}
                    manualSorting={true}
                    manualPagination={true}
                    hidePagination={true}
                    pageSize={10_000}
                    className="overflow-hidden border"
                    onRowClick={(row) => {
                      void navigate({
                        to: "/calastone-orders/$calastoneOrderId",
                        params: { calastoneOrderId: row.original.id },
                      });
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        <TabsContent value="transactions">
          <RelatedLedgerTransactions
            transactions={transfer.ledgerTransactions ?? []}
          />
        </TabsContent>
      </Tabs>
    </AdminPageContainer>
  );
}
